/**
 * A run's summary, made easy to scan: a headline, a few bullets and a recommended next step.
 *
 * New runs are asked to write exactly that shape (runner.SUMMARY_FORMAT):
 *     <headline>
 *     - <point>
 *     Next step: <action>
 * Older runs wrote one long paragraph; those are split here into a headline and bullets
 * (on "(1) … (2) …" markers, else on sentences), so every card reads the same way.
 */

export interface ParsedSummary { headline: string; points: string[]; nextStep: string }

const NEXT_RE = /^(recommended next step|next step|recommendation|recommended action)s?\s*[:\-–]\s*/i;
const BULLET_RE = /^\s*(?:[-•*▪]|\(?\d{1,2}[.)])\s+/;

function sentences(text: string): string[] {
  // Split after . ! ? followed by a space and a capital / digit / quote; keep decimals ("4.2%") intact.
  return text.split(/(?<=[.!?])\s+(?=["'(A-Z0-9])/).map((s) => s.trim()).filter(Boolean);
}

export function parseSummary(text: string): ParsedSummary {
  const lines = (text ?? "").split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  let nextStep = "";
  const points: string[] = [];
  const rest: string[] = [];
  for (const line of lines) {
    if (NEXT_RE.test(line)) nextStep = line.replace(NEXT_RE, "").trim();
    else if (BULLET_RE.test(line)) points.push(line.replace(BULLET_RE, "").trim());
    else rest.push(line);
  }
  let headline = rest.shift() ?? "";
  if (points.length || rest.length) {
    points.push(...rest);
    return { headline, points, nextStep };
  }

  // Legacy: a single paragraph.
  const numbered = headline.split(/\s*\(\d{1,2}\)\s+/);
  if (numbered.length > 2) {
    headline = numbered[0].replace(/[:\s]+$/, "");
    const items = numbered.slice(1);
    // The last numbered item often runs on into closing sentences: keep its first sentence only.
    const last = sentences(items[items.length - 1]);
    items[items.length - 1] = last[0] ?? "";
    const tail = last.slice(1);
    const rec = tail.find((s) => /^(recommend|next|consider|suggest)/i.test(s));
    // Closing sentences are often the real conclusion; drop only bookkeeping ("Five insights written…").
    const extra = tail.filter((s) => s !== rec && !/\b(insights?|findings?|rows?)\b.*\b(written|recorded|saved)\b/i.test(s));
    return { headline: firstSentence(headline), points: [...items.filter(Boolean), ...extra],
             nextStep: nextStep || (rec ?? "") };
  }
  const all = sentences(headline);
  if (all.length <= 2 && headline.length < 220) return { headline, points: [], nextStep };
  const rec = all.findIndex((s, i) => i > 0 && /^(recommend|next step|consider|suggest)/i.test(s));
  const recText = rec > 0 ? all.splice(rec, 1)[0] : "";
  return { headline: all[0], points: all.slice(1, 6), nextStep: nextStep || recText };
}

function firstSentence(s: string): string {
  // "Analyzed 4 Tablet SKUs (…) for year-over-year … declines. Key findings" -> keep the analysis sentence.
  const parts = sentences(s.replace(/\s*Key findings:?$/i, ""));
  return parts.join(" ");
}

/** Headline, bullets and a "Recommended next step" box; onAct turns the step into a linked intent. */
export default function RunSummary({ text, compact = false, onAct }: {
  text: string; compact?: boolean; onAct?: (text: string) => void;
}) {
  const s = parseSummary(text);
  if (!s.headline && !s.points.length) return null;
  if (compact) return <div className="card-answer">{s.headline || s.points[0]}</div>;
  return (
    <div className="run-summary">
      {s.headline && <p className="rs-headline">{s.headline}</p>}
      {s.points.length > 0 && <ul className="rs-points">{s.points.map((p, i) => <li key={i}>{p}</li>)}</ul>}
      {s.nextStep && (
        <div className="next-step">
          <div className="next-step-label">Recommended next step</div>
          <div className="next-step-text">{s.nextStep}</div>
          {onAct && (
            <button type="button" className="btn sm" onClick={(e) => { e.stopPropagation(); onAct(s.nextStep); }}
                    title="Open this as a new intent, linked to this card">⑂ Do this</button>
          )}
        </div>
      )}
    </div>
  );
}
